import { useState, useEffect } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { PREVILLAGES_LIST } from "./rolesResponsibility";
import { useGetAllPrivileges } from "../../../../queries/tenant-setting/use-get-previleges-by-module";
import { useAddRolePrevileges } from "../../../../queries/tenant-setting/use-add-role-previleges";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { getErrorSnackbar } from "../../../../utils/utils";
// import { useQueryClient } from "@tanstack/react-query";
// import LoadingOverlay from "../../../LoadingOverlay";

// Permission columns to display
const PERMISSIONS = ["ALL", "READ_ONLY", "UPDATE"];

type DataItem = {
  id: number;
  roleId: number;
  roleName: string;
  roleType: string;
  roleDescription: string;
  privilegeId: number;
  privilegeName: string;
  privilegeDescription: string;
  privilegeModule: string;
  privilegeAccess: string;
  access: boolean;
};

interface NewRoleTableProps {
  roleId: number;
  viewOnly?: boolean;
}

function NewRoleTable({ roleId, viewOnly = false }: NewRoleTableProps) {
  // console.log("roleId", roleId);

  const { mutateAsync: addRolePrevileges } = useAddRolePrevileges();
  const showSnackbar = useSnackbar();
  // const queryClient = useQueryClient();
  const [data, setData] = useState<DataItem[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<DataItem[]>(
    []
  );
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [selectedPrivilege, setSelectedPrivilege] = useState<string>();
  const {
    data: privilegeList,
    // isLoading,
    // isFetching,
  } = useGetAllPrivileges({
    roleId: roleId,
    getAllPrivilegesModule: selectedPrivilege,
  });

  useEffect(() => {
    // Call API when there are changes, regardless of whether permissions are selected or not
    if (hasChanges) {
      addRolePrevileges({
        rolePrivilegeMapping: selectedPermissions as any,
      })
        .then(() => {
          // showSnackbar(getSuccessSnackbar(res));
          // queryClient.invalidateQueries({
          //   queryKey: ["roles-and-responsibilities-list"],
          // });
          setSelectedPermissions([]);
          setHasChanges(false);
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
          setHasChanges(false);
        });
    }
  }, [selectedPermissions, hasChanges]);

  useEffect(() => {
    if (privilegeList?.data) {
      setData(privilegeList.data as any);
    }
  }, [privilegeList]);

  // Group features by name for each module
  const groupByFeature = (module: string) => {
    const features: Record<string, Record<string, DataItem | undefined>> = {};
    (data || []).forEach((item) => {
      if (item.privilegeModule === module) {
        if (!features[item.privilegeName]) {
          features[item.privilegeName] = {};
        }
        features[item.privilegeName][item.privilegeAccess] = item;
      }
    });
    return features;
  };

  // Handle checkbox change
  const handleCheckboxChange = (
    featureName: string,
    perm: string,
    module: string
  ) => {
    setData((prevData) => {
      // Get all permissions for this feature in this module
      const rowItems = prevData.filter(
        (item) =>
          item.privilegeModule === module && item.privilegeName === featureName
      );
      const otherPerms = PERMISSIONS.filter((p) => p !== "ALL");

      let updatedData: DataItem[];
      if (perm === "ALL") {
        // Toggle all checkboxes in the row
        const isAllChecked = otherPerms.every(
          (p) => rowItems.find((item) => item.privilegeAccess === p)?.access
        );
        updatedData = prevData.map((item) =>
          item.privilegeModule === module && item.privilegeName === featureName
            ? { ...item, access: !isAllChecked }
            : item
        );
      } else {
        // Toggle the individual permission
        const idx = prevData.findIndex(
          (item) =>
            item.privilegeModule === module &&
            item.privilegeName === featureName &&
            item.privilegeAccess === perm
        );
        const newData = prevData.map((item, i) =>
          i === idx ? { ...item, access: !item.access } : item
        );

        // After toggling, check if all other permissions are checked
        const updatedRowItems = newData.filter(
          (item) =>
            item.privilegeModule === module &&
            item.privilegeName === featureName
        );
        const allOthersChecked = otherPerms.every(
          (p) =>
            updatedRowItems.find((item) => item.privilegeAccess === p)?.access
        );

        // Set ALL checkbox accordingly
        updatedData = newData.map((item) =>
          item.privilegeModule === module &&
          item.privilegeName === featureName &&
          item.privilegeAccess === "ALL"
            ? { ...item, access: allOthersChecked }
            : item
        );
      }

      // Update selectedPermissions after setData
      setTimeout(() => {
        updateSelectedPermissions(updatedData, featureName, perm, module);
      }, 0);

      return updatedData;
    });
  };

  const updateSelectedPermissions = (
    updatedData: DataItem[],
    featureName: string,
    _perm: string,
    module: string
  ) => {
    // Get all permissions for this feature in this module
    const rowItems = updatedData.filter(
      (item) =>
        item.privilegeModule === module && item.privilegeName === featureName
    );

    // Always add all row items with their current access values
    // This ensures that when deselecting, we send items with access: false
    // instead of sending an empty payload
    setSelectedPermissions((prev) => [
      ...prev.filter(
        (item) =>
          !(
            item.privilegeModule === module &&
            item.privilegeName === featureName
          )
      ),
      ...rowItems, // Include all row items regardless of access value
    ]);
    setHasChanges(true);
  };

  const [expanded, setExpanded] = useState(
    Array.from({ length: PREVILLAGES_LIST.length }).map(() => false)
  );

  return (
    <div>
      {PREVILLAGES_LIST.map((module, ind) => {
        return (
          <Accordion
            key={module.value}
            expanded={expanded[ind]}
            onChange={() => {
              setExpanded((prev: boolean[]) =>
                prev.map((_: boolean, inx: number) =>
                  inx === ind ? !prev[ind] : false
                )
              );
              setSelectedPrivilege(module.value);
            }}
          >
            <AccordionSummary
              disableRipple
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${module.label}-content`}
              id={`${module.label}header`}
            >
              <Typography>{module.label}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {/* <LoadingOverlay isLoading={is Fetching || isLoading} /> */}
              <TableContainer component={Paper} sx={{ fontSize: "0.85rem" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        Feature Name
                      </TableCell>
                      {PERMISSIONS.map((perm) => (
                        <TableCell
                          key={perm}
                          align="center"
                          sx={{ fontSize: "0.85rem" }}
                        >
                          {perm.replace("_", " ")}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(groupByFeature(module.value)).map(
                      ([featureName, accesses]) => (
                        <TableRow key={featureName}>
                          <TableCell sx={{ fontSize: "0.85rem" }}>
                            {featureName}
                          </TableCell>
                          {PERMISSIONS.map((perm) => {
                            const item = accesses[perm];
                            let checked = !!item?.access;

                            if (perm === "ALL") {
                              // "ALL" is checked if all other permissions are checked
                              const otherChecked = PERMISSIONS.filter(
                                (p) => p !== "ALL"
                              ).every((p) => accesses[p]?.access);
                              checked = otherChecked;
                            }

                            return (
                              <TableCell
                                align="center"
                                key={perm}
                                sx={{ fontSize: "0.85rem" }}
                              >
                                <Checkbox
                                  disabled={viewOnly}
                                  checked={checked}
                                  onChange={() =>
                                    handleCheckboxChange(
                                      featureName,
                                      perm,
                                      module.value
                                    )
                                  }
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default NewRoleTable;
